import { useState, useEffect, useRef } from 'react'
import { generateReadingPlan, saveReadingPlanDraft, approveReadingPlan, publishReadingPlan } from '../hooks/useSermons'

// ── READING PLAN REVIEW SHEET (admin) ─────────────────────────────────────
// Full-screen sheet opened from FullSermonSheet's "Reading plan" section.
// With no existingPlan it auto-generates a 5-day draft via Claude on open;
// with an existingPlan it loads those days straight into the editor. The
// admin edits each day inline, then Save draft / Approve / Publish.
//
// Props:
//   sermon       — the canonical sermon row (id, church_id, title, preacher,
//                   date, transcript, youtube_id, youtube_url)
//   sermonRefs   — this sermon's sermon_refs rows (verse_ref, timestamp, notes)
//   existingPlan — { id, title, summary, status, days: [...] } | null
//   userId       — current user's id
//   onClose()
//   onSaved(plan) — called after a successful save/approve/publish

const GOLD = 'var(--gold)'
const SERIF = "'Source Serif 4', Georgia, serif"
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif"

export default function ReadingPlanReviewSheet({ sermon, sermonRefs, existingPlan, userId, onClose, onSaved }) {
  const [draft, setDraft] = useState(existingPlan ? {
    title: existingPlan.title || '',
    summary: existingPlan.summary || '',
    days: (existingPlan.days || []).map(d => ({ ...d })),
  } : null)
  const [loading, setLoading] = useState(!existingPlan)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(null) // 'save' | 'approve' | 'publish' | null
  const generatedOnce = useRef(false)

  useEffect(() => {
    if (existingPlan || generatedOnce.current) return
    generatedOnce.current = true
    generate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function generate() {
    setLoading(true); setError(null)
    try {
      const result = await generateReadingPlan(sermon, sermonRefs)
      setDraft(result)
    } catch (err) {
      setError(err?.message || 'Something went wrong generating the plan.')
    } finally {
      setLoading(false)
    }
  }

  function updateDay(dayNumber, field, value) {
    setDraft(d => ({
      ...d,
      days: d.days.map(day => day.day_number === dayNumber ? { ...day, [field]: value } : day),
    }))
  }

  async function doSave(then) {
    if (!draft) return
    setBusy(then || 'save'); setError(null)
    try {
      const plan = await saveReadingPlanDraft({
        planId:    existingPlan?.id,
        sermonId:  sermon.id,
        churchId:  sermon.church_id,
        userId,
        title:     draft.title,
        summary:   draft.summary,
        days:      draft.days,
      })
      if (then === 'approve') await approveReadingPlan(plan.id, userId)
      if (then === 'publish') await publishReadingPlan(plan.id, userId)
      onSaved?.(plan)
    } catch (err) {
      setError(err?.message || 'Could not save the plan.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div style={S.backdrop} onClick={e => { if (e.target === e.currentTarget) onClose?.() }}>
      <style>{'@keyframes sermon-spin { to { transform: rotate(360deg); } }'}</style>
      <div style={S.sheet}>
        <div style={S.header}>
          <div style={{ fontFamily: SERIF, fontSize: 18, color: 'var(--ink)' }}>Reading plan</div>
          <button onClick={onClose} style={S.iconBtn}>✕</button>
        </div>

        <div style={S.scroll}>
          {loading ? (
            <div style={S.centerNote}>
              <span style={S.spinner}/>
              <div style={{ marginTop: 14 }}>Generating 5-day plan from this sermon…</div>
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--stone)' }}>This usually takes 10-20 seconds</div>
            </div>
          ) : error && !draft ? (
            <div style={S.centerNote}>
              <div style={{ color: '#C8372A', marginBottom: 14 }}>{error}</div>
              <button onClick={generate} style={S.outlineBtn}>Try again</button>
            </div>
          ) : draft ? (
            <>
              <Field label="Plan title">
                <input style={S.input} value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}/>
              </Field>
              <Field label="Summary">
                <textarea style={{ ...S.input, minHeight: 50, resize: 'vertical' }} value={draft.summary || ''} onChange={e => setDraft(d => ({ ...d, summary: e.target.value }))}/>
              </Field>

              {draft.days.map(day => (
                <div key={day.day_number} style={S.dayCard}>
                  <div style={S.dayLabel}>DAY {day.day_number}</div>
                  <Field label="Title">
                    <input style={S.input} value={day.title || ''} onChange={e => updateDay(day.day_number, 'title', e.target.value)}/>
                  </Field>
                  <Field label="Passage">
                    <input style={S.input} value={day.passage_ref} onChange={e => updateDay(day.day_number, 'passage_ref', e.target.value)}/>
                  </Field>
                  <Field label="Devotional">
                    <textarea style={{ ...S.input, minHeight: 110, resize: 'vertical' }} value={day.devotional} onChange={e => updateDay(day.day_number, 'devotional', e.target.value)}/>
                  </Field>
                  <Field label="Reflection question">
                    <textarea style={{ ...S.input, minHeight: 44, resize: 'vertical' }} value={day.reflection} onChange={e => updateDay(day.day_number, 'reflection', e.target.value)}/>
                  </Field>
                  {day.watch_label && (
                    <div style={S.watchNote}>▶ {day.watch_label}</div>
                  )}
                </div>
              ))}

              {error && <div style={{ color: '#C8372A', fontSize: 13, marginTop: 4, marginBottom: 10 }}>{error}</div>}
            </>
          ) : null}
        </div>

        {draft && !loading && (
          <div style={S.footer}>
            <button disabled={!!busy} onClick={() => doSave('save')} style={{ ...S.outlineBtn, ...(busy ? S.dis : {}) }}>
              {busy === 'save' ? 'Saving…' : 'Save draft'}
            </button>
            <button disabled={!!busy} onClick={() => doSave('approve')} style={{ ...S.outlineBtn, ...(busy ? S.dis : {}) }}>
              {busy === 'approve' ? 'Approving…' : 'Approve'}
            </button>
            <button disabled={!!busy} onClick={() => doSave('publish')} style={{ ...S.primaryBtn, ...(busy ? S.dis : {}) }}>
              {busy === 'publish' ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--stone)', marginBottom: 6, fontFamily: SANS }}>{label}</label>
      {children}
    </div>
  )
}

const S = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', fontFamily: SANS },
  sheet: { background: 'var(--white)', width: '100%', maxWidth: 480, margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' },
  header: { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--border)', minHeight: 54 },
  iconBtn: { background: 'rgba(31,27,22,0.06)', border: 'none', color: 'var(--stone)', width: 34, height: 34, borderRadius: 999, cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  scroll: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '18px 16px calc(env(safe-area-inset-bottom) + 20px)' },
  centerNote: { textAlign: 'center', color: 'var(--stone)', fontSize: 14, padding: '60px 10px' },
  spinner: { display: 'inline-block', width: 26, height: 26, border: `2.5px solid ${GOLD}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'sermon-spin 0.8s linear infinite' },
  input: { width: '100%', background: 'var(--white)', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '10px 12px', color: 'var(--ink)', fontSize: 14, fontFamily: SANS, boxSizing: 'border-box', outline: 'none' },
  dayCard: { background: 'rgba(31,27,22,0.03)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 14px', marginBottom: 16 },
  dayLabel: { fontFamily: SANS, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', color: 'var(--gold-ink)', marginBottom: 12 },
  watchNote: { fontSize: 12.5, color: 'var(--stone)', marginTop: 2 },
  footer: { flexShrink: 0, display: 'flex', gap: 10, padding: '12px 16px calc(env(safe-area-inset-bottom) + 14px)', borderTop: '1px solid var(--border)' },
  outlineBtn: { flex: 1, background: 'transparent', border: `1px solid ${GOLD}`, color: 'var(--gold-ink)', borderRadius: 10, padding: '11px 10px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: SANS },
  primaryBtn: { flex: 1, background: GOLD, border: 'none', color: 'var(--ink)', borderRadius: 10, padding: '11px 10px', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', fontFamily: SANS },
  dis: { opacity: 0.5, cursor: 'default' },
}
